import { useCallback, useState } from "react"
import { Check, ChevronDown, Link2, Share2 } from "lucide-react"
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  LineShareButton,
  LineIcon,
  LinkedinShareButton,
  LinkedinIcon,
  RedditShareButton,
  RedditIcon,
  TwitterShareButton,
  WhatsappShareButton,
  WhatsappIcon,
  XIcon,
} from "react-share"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

function InstagramIcon({ size = 30, round }: { size?: number; round?: boolean }) {
  const r = round ? size / 2 : size * 0.15
  return (
    <svg viewBox="0 0 64 64" width={size} height={size}>
      <rect width="64" height="64" rx={r} fill="url(#ig-grad)" />
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <g transform="translate(14,14) scale(0.5625)">
        <path
          fill="#fff"
          d="M32 18.4c4.4 0 5 0 6.7.1 1.6.1 2.5.3 3.1.5.8.3 1.3.6 1.9 1.2.6.6.9 1.1 1.2 1.9.2.6.4 1.5.5 3.1.1 1.7.1 2.3.1 6.7s0 5-.1 6.7c-.1 1.6-.3 2.5-.5 3.1-.3.8-.6 1.3-1.2 1.9-.6.6-1.1.9-1.9 1.2-.6.2-1.5.4-3.1.5-1.7.1-2.3.1-6.7.1s-5 0-6.7-.1c-1.6-.1-2.5-.3-3.1-.5-.8-.3-1.3-.6-1.9-1.2-.6-.6-.9-1.1-1.2-1.9-.2-.6-.4-1.5-.5-3.1-.1-1.7-.1-2.3-.1-6.7s0-5 .1-6.7c.1-1.6.3-2.5.5-3.1.3-.8.6-1.3 1.2-1.9.6-.6 1.1-.9 1.9-1.2.6-.2 1.5-.4 3.1-.5 1.7-.1 2.3-.1 6.7-.1zm0-3.6c-4.5 0-5.1 0-6.8.1-1.7.1-2.9.3-3.9.7-1.1.4-2 1-2.9 1.9-.9.9-1.5 1.8-1.9 2.9-.4 1-.6 2.2-.7 3.9-.1 1.7-.1 2.3-.1 6.8s0 5.1.1 6.8c.1 1.7.3 2.9.7 3.9.4 1.1 1 2 1.9 2.9.9.9 1.8 1.5 2.9 1.9 1 .4 2.2.6 3.9.7 1.7.1 2.3.1 6.8.1s5.1 0 6.8-.1c1.7-.1 2.9-.3 3.9-.7 1.1-.4 2-1 2.9-1.9.9-.9 1.5-1.8 1.9-2.9.4-1 .6-2.2.7-3.9.1-1.7.1-2.3.1-6.8s0-5.1-.1-6.8c-.1-1.7-.3-2.9-.7-3.9-.4-1.1-1-2-1.9-2.9-.9-.9-1.8-1.5-2.9-1.9-1-.4-2.2-.6-3.9-.7-1.7-.1-2.3-.1-6.8-.1zm0 9.7a7.5 7.5 0 100 15 7.5 7.5 0 000-15zm0 12.4a4.9 4.9 0 110-9.8 4.9 4.9 0 010 9.8zm7.8-12.7a1.8 1.8 0 11-3.5 0 1.8 1.8 0 013.5 0z"
        />
      </g>
    </svg>
  )
}

interface SharePopoverProps {
  url: string
  title: string
  description?: string
  className?: string
}

/* eslint-disable @typescript-eslint/no-explicit-any */
interface Platform {
  name: string
  Button: React.ElementType<any>
  Icon: React.ElementType<any>
  props: Record<string, unknown>
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function SharePopover({
  url,
  title,
  description,
  className,
}: SharePopoverProps) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)

  const handleClick = useCallback(async () => {
    // Mobile: try native share sheet first
    if (window.innerWidth < 640 && navigator.share) {
      try {
        await navigator.share({ url, title, text: description })
        return
      } catch {
        // User cancelled or not supported — fall through to popover
      }
    }
    setOpen(true)
  }, [url, title, description])

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success("Link copied!")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Failed to copy link")
    }
  }, [url])

  const beforeShare = useCallback(() => {
    setOpen(false)
    return Promise.resolve()
  }, [])

  const handleInstagramShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success("Link copied! Opening Instagram…")
      setOpen(false)
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer")
    } catch {
      toast.error("Failed to copy link")
    }
  }, [url])

  const primaryPlatforms: Platform[] = [
    { name: "LINE", Button: LineShareButton, Icon: LineIcon, props: { url, title } },
    { name: "Instagram", Button: "button" as unknown as React.ElementType, Icon: InstagramIcon, props: { type: "button", onClick: handleInstagramShare } },
    { name: "Facebook", Button: FacebookShareButton, Icon: FacebookIcon, props: { url } },
    { name: "WhatsApp", Button: WhatsappShareButton, Icon: WhatsappIcon, props: { url, title, separator: " — " } },
  ]

  const secondaryPlatforms: Platform[] = [
    { name: "X", Button: TwitterShareButton, Icon: XIcon, props: { url, title } },
    { name: "Reddit", Button: RedditShareButton, Icon: RedditIcon, props: { url, title } },
    { name: "LinkedIn", Button: LinkedinShareButton, Icon: LinkedinIcon, props: { url, title, summary: description } },
    { name: "Email", Button: EmailShareButton, Icon: EmailIcon, props: { url, subject: title, body: description ? `${description}\n\n${url}` : url } },
  ]

  const renderPlatformGrid = (platforms: Platform[]) => (
    <div className="grid grid-cols-4 gap-1 px-3 py-2.5">
      {platforms.map(({ name, Button: ShareButton, Icon, props: shareProps }) => (
        <ShareButton
          key={name}
          {...shareProps}
          {...(name !== "Instagram" ? { beforeOnClick: beforeShare } : {})}
          className={cn(
            "group flex flex-col items-center gap-1 rounded-lg py-1.5 outline-none transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50",
            name === "Instagram" && "cursor-pointer border-0 bg-transparent p-0"
          )}
        >
          <div className="transition-all duration-200 grayscale group-hover:scale-110 group-hover:grayscale-0">
            <Icon size={30} round />
          </div>
          <span className="text-[10px] font-medium text-muted-foreground transition-colors group-hover:text-foreground">
            {name}
          </span>
        </ShareButton>
      ))}
    </div>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={handleClick}
          className={cn(
            "shrink-0 border-slate-200 dark:border-slate-700",
            className
          )}
          title="Share this phrase"
        >
          <Share2 className="size-4" />
        </Button>
      </PopoverAnchor>

      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="w-56 overflow-hidden rounded-xl border-slate-200 p-0 shadow-lg dark:border-slate-700"
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Share
          </p>
        </div>

        {/* Copy Link */}
        <button
          type="button"
          onClick={copyLink}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
        >
          <div
            className={cn(
              "flex size-7 items-center justify-center rounded-full transition-colors",
              copied
                ? "bg-emerald-100 dark:bg-emerald-900/40"
                : "bg-slate-100 dark:bg-slate-800"
            )}
          >
            {copied ? (
              <Check className="size-3.5 text-emerald-600" />
            ) : (
              <Link2 className="size-3.5 text-slate-500 dark:text-slate-400" />
            )}
          </div>
          <span
            className={cn(
              "text-sm font-medium transition-colors",
              copied ? "text-emerald-600" : "text-foreground"
            )}
          >
            {copied ? "Copied!" : "Copy link"}
          </span>
        </button>

        {/* Divider */}
        <div className="mx-3 border-t border-slate-100 dark:border-slate-800" />

        {/* Primary Platforms */}
        {renderPlatformGrid(primaryPlatforms)}

        {/* More Options */}
        <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
          <div className="mx-3 border-t border-slate-100 dark:border-slate-800" />
          <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground">
            <span>More options</span>
            <ChevronDown
              className={cn(
                "size-3 transition-transform duration-200",
                moreOpen && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            {renderPlatformGrid(secondaryPlatforms)}
          </CollapsibleContent>
        </Collapsible>
      </PopoverContent>
    </Popover>
  )
}
